import MockAdapter from "axios-mock-adapter"
import type { CandidateDto, ApplicationDto, CandidateMatchDto, JobMatchScoreDto } from "@/modules/recruitment/infrastructure/recruitment-service"
import { VacancyStatus } from "@/modules/recruitment/enums/VacancyStatus"
import { mockVacancies } from "./mock-vacancy-service"

// Mock Candidates Data
const mockCandidates: CandidateDto[] = [
  {
    id: "candidate-1",
    firstName: "Sarah",
    lastName: "Johnson",
    email: "sarah.johnson@example.com",
    phone: "+27 82 123 4567",
    location: "Cape Town, South Africa",
    experienceYears: 6,
    education: "BSc Computer Science, University of Cape Town",
    skills: ["React", "TypeScript", "Node.js", "AWS", "Redux", "CSS"],
    cvExtractionId: "cv-1",
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    applicationCount: 2,
  },
  {
    id: "candidate-2",
    firstName: "Michael",
    lastName: "Chen",
    email: "michael.chen@example.com",
    phone: "+27 83 234 5678",
    location: "Johannesburg, South Africa",
    experienceYears: 4,
    education: "BSc Information Technology, Wits University",
    skills: ["Docker", "Kubernetes", "AWS", "Python", "Terraform", "Jenkins"],
    cvExtractionId: "cv-2",
    createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    applicationCount: 1,
  },
  {
    id: "candidate-3",
    firstName: "Emily",
    lastName: "Williams",
    email: "emily.williams@example.com",
    phone: "+27 84 345 6789",
    location: "Durban, South Africa",
    experienceYears: 3,
    education: "BSc Computer Science, UKZN",
    skills: ["React", "JavaScript", "HTML", "CSS", "Git"],
    cvExtractionId: "cv-3",
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    applicationCount: 1,
  },
]

// Mock Applications Data
const mockApplications: ApplicationDto[] = [
  {
    id: "app-1",
    jobId: "job-1",
    candidateId: "candidate-1",
    organizationId: "org-1",
    currentStage: "Interview",
    appliedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    stageChangedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    matchScore: 85,
    nextAction: "Schedule technical interview",
    vacancy: mockVacancies[0],
    candidate: mockCandidates[0],
  },
  {
    id: "app-2",
    jobId: "job-2",
    candidateId: "candidate-2",
    organizationId: "org-1",
    currentStage: "PhoneScreen",
    appliedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    stageChangedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    matchScore: 78,
    nextAction: "Conduct phone screening",
    vacancy: mockVacancies[1],
    candidate: mockCandidates[1],
  },
  {
    id: "app-3",
    jobId: "job-1",
    candidateId: "candidate-3",
    organizationId: "org-1",
    currentStage: "Applied",
    appliedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    stageChangedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    matchScore: 62,
    nextAction: "Review application",
    vacancy: mockVacancies[0],
    candidate: mockCandidates[2],
  },
]

export const registerRecruitmentMocks = (mock: MockAdapter) => {

  // Get job applications
  mock.onGet(/\/api\/job\/([^/]+)\/applications$/).reply((config) => {
    const urlParts = config.url?.split("/") || []
    const jobId = urlParts[urlParts.length - 2]

    const applications = mockApplications.filter(app => app.jobId === jobId)
    return [200, applications]
  })

  // Match candidates to job
  mock.onGet(/\/api\/job\/([^/]+)\/match-candidates$/).reply((config) => {
    const urlParts = config.url?.split("/") || []
    const jobId = urlParts[urlParts.length - 2]
    const topN = parseInt(config.params?.topN || "10")

    const job = mockVacancies.find(j => j.id === jobId)
    if (!job) {
      return [404, { message: "Job not found" }]
    }

    // Mock matching logic
    const matches: CandidateMatchDto[] = mockCandidates.map(candidate => {
      const requiredSkillNames = job.requiredSkills.map(s => s.name)
      const matchedSkills = candidate.skills.filter(skill =>
        requiredSkillNames.includes(skill)
      )
      const skillsMatch = (matchedSkills.length / requiredSkillNames.length) * 100
      const experienceMatch = Math.min((candidate.experienceYears || 0) / job.yearsOfExperience * 100, 100)
      const matchScore = (skillsMatch * 0.6 + experienceMatch * 0.4)

      return {
        candidateId: candidate.id,
        candidateName: `${candidate.firstName} ${candidate.lastName}`,
        email: candidate.email,
        matchScore: Math.round(matchScore),
        skillsMatch: Math.round(skillsMatch),
        experienceMatch: Math.round(experienceMatch),
        matchedSkills,
        missingSkills: requiredSkillNames.filter(skill => !candidate.skills.includes(skill)),
        recommendation: matchScore >= 70 ? "Strong match" : matchScore >= 50 ? "Good potential" : "Consider for screening",
      }
    }).sort((a, b) => b.matchScore - a.matchScore).slice(0, topN)

    return [200, matches]
  })

  // Calculate job match score
  mock.onGet(/\/api\/job\/([^/]+)\/match-score$/).reply((config) => {
    const urlParts = config.url?.split("/") || []
    const jobId = urlParts[urlParts.length - 2]
    const candidateId = config.params?.candidateId

    const job = mockVacancies.find(j => j.id === jobId)
    const candidate = mockCandidates.find(c => c.id === candidateId)

    if (!job || !candidate) {
      return [404, { message: "Job or candidate not found" }]
    }

    const requiredSkillNames = job.requiredSkills.map(s => s.name)
    const matchedSkills = candidate.skills.filter(skill =>
      requiredSkillNames.includes(skill)
    )
    const skillScore = (matchedSkills.length / requiredSkillNames.length) * 100
    const experienceScore = Math.min((candidate.experienceYears || 0) / job.yearsOfExperience * 100, 100)
    const overallScore = (skillScore * 0.6 + experienceScore * 0.4)

    const matchScoreDto: JobMatchScoreDto = {
      jobId,
      jobTitle: job.title,
      candidateId,
      candidateName: `${candidate.firstName} ${candidate.lastName}`,
      overallScore: Math.round(overallScore),
      skillScore: Math.round(skillScore),
      experienceScore: Math.round(experienceScore),
      matchedSkills,
      missingSkills: requiredSkillNames.filter(skill => !candidate.skills.includes(skill)),
      recommendation: overallScore >= 70 ? "Highly recommended" : overallScore >= 50 ? "Recommended" : "Consider alternatives",
      details: `Candidate has ${matchedSkills.length} out of ${requiredSkillNames.length} required skills and ${candidate.experienceYears} years of experience (${job.yearsOfExperience} required).`,
    }

    return [200, matchScoreDto]
  })

  // Get all candidates
  mock.onGet(/\/api\/candidate(\?.*)?$/).reply((config) => {
    const searchTerm = config.params?.searchTerm?.toLowerCase()

    if (searchTerm) {
      const filtered = mockCandidates.filter(c =>
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchTerm) ||
        c.email.toLowerCase().includes(searchTerm) ||
        c.skills.some(s => s.toLowerCase().includes(searchTerm))
      )
      return [200, filtered]
    }

    return [200, mockCandidates]
  })

  // Get candidate by ID
  mock.onGet(/\/api\/candidate\/([^/]+)$/).reply((config) => {
    const id = config.url?.split("/").pop()
    const candidate = mockCandidates.find(c => c.id === id)

    if (candidate) {
      return [200, candidate]
    }
    return [404, { message: "Candidate not found" }]
  })

  // Upload CV and create candidate
  mock.onPost("/api/candidate/upload-cv").reply((_config) => {
    // Mock CV extraction
    const newCandidate: CandidateDto = {
      id: `candidate-${Date.now()}`,
      firstName: "New",
      lastName: "Candidate",
      email: "new.candidate@example.com",
      phone: "+27 82 000 0000",
      location: "Cape Town, South Africa",
      experienceYears: 3,
      education: "BSc Computer Science",
      skills: ["React", "JavaScript", "HTML", "CSS"],
      cvExtractionId: `cv-${Date.now()}`,
      createdAt: new Date().toISOString(),
      applicationCount: 0,
    }

    mockCandidates.push(newCandidate)
    return [201, newCandidate]
  })

  // Get all applications
  mock.onGet(/\/api\/application(\?.*)?$/).reply((config) => {
    const jobId = config.params?.jobId
    const stage = config.params?.stage

    let results = [...mockApplications]

    if (jobId) {
      results = results.filter(app => app.jobId === jobId)
    }

    if (stage) {
      results = results.filter(app => app.currentStage === stage)
    }

    return [200, results]
  })

  // Get application by ID
  mock.onGet(/\/api\/application\/([^/]+)$/).reply((config) => {
    const id = config.url?.split("/").pop()?.split("?")[0]
    const application = mockApplications.find(app => app.id === id)

    if (application) {
      return [200, application]
    }
    return [404, { message: "Application not found" }]
  })

  // Create application
  mock.onPost("/api/application").reply((config) => {
    const { jobId, candidateId } = JSON.parse(config.data ?? "{}")

    const job = mockVacancies.find(j => j.id === jobId)
    const candidate = mockCandidates.find(c => c.id === candidateId)

    if (!job || !candidate) {
      return [404, { message: "Job or candidate not found" }]
    }

    const newApplication: ApplicationDto = {
      id: `app-${Date.now()}`,
      jobId,
      candidateId,
      organizationId: job.organizationId,
      currentStage: "Applied",
      appliedDate: new Date().toISOString(),
      stageChangedAt: new Date().toISOString(),
      matchScore: Math.floor(Math.random() * 40) + 60, // Random score between 60-100
      nextAction: "Review application",
      vacancy: job,
      candidate,
    }

    mockApplications.push(newApplication)
    job.applicationCount++
    candidate.applicationCount++

    return [201, newApplication]
  })

  // Update application stage
  mock.onPut(/\/api\/application\/([^/]+)\/stage$/).reply((config) => {
    const urlParts = config.url?.split("/") || []
    const id = urlParts[urlParts.length - 2]
    const { newStage } = JSON.parse(config.data ?? "{}")

    const appIndex = mockApplications.findIndex(app => app.id === id)
    if (appIndex !== -1) {
      mockApplications[appIndex].currentStage = newStage
      mockApplications[appIndex].stageChangedAt = new Date().toISOString()
      return [200, { message: "Stage updated" }]
    }
    return [404, { message: "Application not found" }]
  })

  // Delete application
  mock.onDelete(/\/api\/application\/([^/]+)$/).reply((config) => {
    const id = config.url?.split("/").pop()
    const appIndex = mockApplications.findIndex(app => app.id === id)

    if (appIndex !== -1) {
      const app = mockApplications[appIndex]
      mockApplications.splice(appIndex, 1)

      // Update counts
      const job = mockVacancies.find(j => j.id === app.jobId)
      const candidate = mockCandidates.find(c => c.id === app.candidateId)
      if (job) job.applicationCount--
      if (candidate) candidate.applicationCount--

      return [204]
    }
    return [404, { message: "Application not found" }]
  })

  // Get recruitment dashboard
  mock.onGet("/api/recruitment/dashboard").reply(() => {
    const activeJobs = mockVacancies.filter(j => j.status === VacancyStatus.Active).length
    const totalApplications = mockApplications.length
    const interviewStage = mockApplications.filter(a => a.currentStage === "Interview").length

    return [200, {
      activeJobs,
      totalApplications,
      interviewStage,
      topVacancies: mockVacancies.sort((a, b) => b.applicationCount - a.applicationCount).slice(0, 5).map(job => ({
        jobId: job.id,
        title: job.title,
        applicationCount: job.applicationCount,
      })),
      avgMatchScore: mockApplications.reduce((sum, app) => sum + app.matchScore, 0) / mockApplications.length || 0,
    }]
  })

  // Get recruitment stats
  mock.onGet("/api/recruitment/stats").reply(() => {
    return [200, {
      totalJobs: mockVacancies.length,
      activeJobs: mockVacancies.filter(j => j.status === "Active").length,
      totalCandidates: mockCandidates.length,
      totalApplications: mockApplications.length,
      applicationsByStage: {
        Applied: mockApplications.filter(a => a.currentStage === "Applied").length,
        PhoneScreen: mockApplications.filter(a => a.currentStage === "PhoneScreen").length,
        Interview: mockApplications.filter(a => a.currentStage === "Interview").length,
        TechnicalTest: mockApplications.filter(a => a.currentStage === "TechnicalTest").length,
        Offer: mockApplications.filter(a => a.currentStage === "Offer").length,
        Hired: mockApplications.filter(a => a.currentStage === "Hired").length,
        Rejected: mockApplications.filter(a => a.currentStage === "Rejected").length,
      },
    }]
  })

  // Get hired candidates
  mock.onGet("/api/recruitment/hired").reply(() => {
    const hiredApplications = mockApplications.filter(a => a.currentStage === "Hired")
    return [200, hiredApplications]
  })

  return mock
}
